import React from 'react'
import PropTypes from 'prop-types'
import Highlighter from 'react-highlight-words'
import Select from 'react-select'
import Creatable from 'react-select/creatable'
import Async from 'react-select/async'
import AsyncCreatable from 'react-select/async-creatable'
import { FormikContext, ErrorMessage } from 'formik'
import clsx from 'clsx'
import { withAsyncPaginate } from 'react-select-async-paginate'

import lang from '@/lang'

import styles from './select-input.css'
import ArrowIcon from '@assets/images/icons/arr_down.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconHelp from '@assets/images/icons/help.svg'
import ErrorIcon from '@assets/images/icons/v2/error.svg'
import Tooltip from '@mui/material/Tooltip'
import { FillTransition } from '@/modules/base/transitions/index'
import theme from '@/theme'

import { defaultStyles, inlineStyles } from './select-input.styles'
import MultiValueContainerWithToolTip from './multi-value-container-with-tooltip'
import MenuComponentWithWidth from './menu-component-with-width'
import BankingLogo from '@/modules/banking/components/banking-logo'
import ColorBubble from '../color-bubble/color-bubble'
import RequiredMark from '@/modules/base/components/texts/required-mark/required-mark'

const AsyncPaginateCreatable = withAsyncPaginate(Creatable)
const AsyncPaginateSelect = withAsyncPaginate(Select)

class SelectInput extends React.Component {
  constructor(props) {
    super(props)

    const { value, options, customSelectOptions } = props
    this.state = {
      input: '',
      value,
      options,
    }

    this.labelPrefixKey = customSelectOptions && customSelectOptions.labelPrefixKey ? customSelectOptions.labelPrefixKey : 'labelPrefix'
    this.labelSuffixKey = customSelectOptions && customSelectOptions.labelPrefixKey ? customSelectOptions.labelPrefixKey : 'labelSuffix'
    this.labelKey = customSelectOptions && customSelectOptions.labelKey ? customSelectOptions.labelKey : 'label'
    this.valueKey = customSelectOptions && customSelectOptions.valueKey ? customSelectOptions.valueKey : 'value'
    this.useDefault = customSelectOptions?.useDefault
    this.isValidNewOption = this.isValidNewOption.bind(this)
    this.onInputChange = this.onInputChange.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onOptionCreated = this.onOptionCreated.bind(this)
    this.getValue = this.getValue.bind(this)
    this.isValueEqual = this.isValueEqual.bind(this)
    this.getSelectInput = this.getSelectInput.bind(this)
    // Callback ref replacing the legacy string ref (ref={name} + this.refs[name]), which
    // React deprecates. Exactly one select variant is rendered per instance, so a single
    // slot is enough; getSelectInput() stays the public accessor for outside callers.
    this.selectRef = null
    this.setSelectRef = (element) => { this.selectRef = element }
    this.renderOptionLabel = this.renderOptionLabel.bind(this)
  }

  renderOptionLabel(option, inputValue) {
    const { customSelectOptions, createLabel, showColorBubble = false } = this.props

    const suffix = option[this.labelSuffixKey] ? `${customSelectOptions.labelSuffixDelimiter || ''}${option[this.labelSuffixKey]}` : ''
    const prefix = option[this.labelPrefixKey] ? `${option[this.labelPrefixKey]}${customSelectOptions.labelPrefixDelimiter || ''}` : ''
    const label = `${prefix}${option[this.labelKey] || ''}${suffix}`

    const colorBubble = showColorBubble ? <ColorBubble color={option.backgroundColor || theme.newTagColor} /> : null

    if (option.value === 'internal') {
      return (
        <div className={styles.ownCompanyOption}>
          {option[this.labelKey]}
        </div>
      )
    }

    if (option.isNewOption) {
      return (
        <div className={styles.newOption}>
          '<b>{label}</b>' {createLabel || lang.create}
        </div>
      )
    }

    if (option.icon) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {option.icon}
          <span style={{ marginLeft: 10 }}>{option[this.labelKey]}</span>
        </div>
      )
    } else if (option.iconUrl) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={option.iconUrl}
            style={{ width: 15 }}
            alt={'icon'}
          />
          <span style={{ marginLeft: 10 }}>
            <Highlighter
              searchWords={[inputValue]}
              textToHighlight={label || ''}
              highlightClassName={styles.matchedText}
              autoEscape={true}
            />
          </span>
        </div>
      )
    } else if (option.bankingLogo) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BankingLogo accountType={option.bankingLogo.accountType} url={option.bankingLogo.bankLogoSmall} />
          <span style={{ marginLeft: 10 }}>{option[this.labelKey]}</span>
        </div>
      )
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {colorBubble}
        <Highlighter
          searchWords={[inputValue]}
          textToHighlight={label || ''}
          highlightClassName={styles.matchedText}
          autoEscape={true}
        />
      </div>
    )
  }

  isValueEqual(value, otherValue) {
    if (value === otherValue) {
      return true
    } else {
      if (typeof value === 'object' && typeof otherValue === 'object' && value !== null && otherValue !== null) {
        const { customSelectOptions } = this.props
        if (customSelectOptions?.isMulti) {
          if (value.length !== otherValue.length) {
            return false
          } else {
            return value.every(item => otherValue.find(otherItem => otherItem[this.valueKey] === item[this.valueKey]))
          }
        } else {
          return value[this.valueKey] === otherValue[this.valueKey]
        }
      }
      return false
    }
  }

  getValue() {
    const { name, customSelectOptions } = this.props
    const fieldMeta = this.props.useFormik === undefined || this.props.useFormik ? this.context?.getFieldMeta(name) : null
    let value

    if (fieldMeta) {
      value = fieldMeta.value
      if (this.valueKey && typeof value !== 'object') {
        value = { [this.valueKey]: value }
        if (customSelectOptions?.isMulti) {
          value = [value]
        }
      }
    } else {
      value = this.props.value
    }

    return value
  }

  componentDidMount() {
    const { value: currentValue } = this.state

    const value = this.getValue()

    if (!this.isValueEqual(value, currentValue)) {
      this.setState(state => ({ ...state, value }))
    }
  }

  componentDidUpdate() {
    const { options } = this.props
    const { value: currentValue, options: currentOptions } = this.state

    if (options && (options.length !== currentOptions.length || JSON.stringify(options) !== JSON.stringify(currentOptions))) {
      this.setState({ options })
    }

    const value = this.getValue()

    if (!this.isValueEqual(value, currentValue)) {
      this.setState({ value })
    }
  }

  getCustomStyles() {
    const { isInline, styles: externalStyles = {}, dropDownMaxHeight, errorMessage } = this.props

    const currentStyle = isInline ? inlineStyles : defaultStyles
    const newStyle = { ...currentStyle }

    newStyle.menuList = (base) => ({
      ...base,
      ...(dropDownMaxHeight && { maxHeight: dropDownMaxHeight }),
    })

    if (errorMessage) {
      const baseControl = newStyle.control
      newStyle.control = (base, state) => ({
        ...(baseControl ? baseControl(base, state) : base),
        borderColor: theme.red,
        '&:hover': { borderColor: theme.red },
      })
    }

    for (const key in externalStyles) {
      if (Object.hasOwnProperty.call(externalStyles, key)) {
        newStyle[key] = (base, state) => {
          let newBase = base
          if (Object.hasOwnProperty.call(currentStyle, key)) {
            newBase = currentStyle[key](base, state)
          }
          return externalStyles[key](newBase, state)
        }
      }
    }

    return newStyle
  }

  onFocus() {
    this.props.onFocus && this.props.onFocus()
  }

  onBlur(inputValue) {
    const { createOnBlur } = this.props
    if (createOnBlur && inputValue) {
      const { currentTarget: { value } } = inputValue
      this.onOptionCreated(value)
    }
    this.props.onBlur && this.props.onBlur()
  }

  onInputChange(option, { action }) {
    const { name, handleChange, handleMenuClose, useFormik } = this.props
    const valueProp = this.valueKey

    const fieldHelper = useFormik === undefined || useFormik ? this.context?.getFieldHelpers(name) : null
    switch (action) {
      case 'select-option':
        if (option.isNewOption) {
          return this.onOptionCreated(option ? option[valueProp] : null)
        } else {
          const newValue = option ? option[valueProp] : null
          fieldHelper && fieldHelper.setValue(newValue)
          return this.setState({ value: newValue }, () => {
            handleChange && handleChange(option, name)
          })
        }
      case 'pop-value':
      case 'clear':
      case 'remove-value':
        fieldHelper && fieldHelper.setValue(option)
        return this.setState({ value: option }, () => {
          handleChange && handleChange(option, name)
        })
      case 'menu-close':
        handleMenuClose && handleMenuClose()
        return
    }
  }

  onOptionCreated(inputValue) {
    const { options } = this.state
    const { onOptionCreated } = this.props

    const newOptions = [...options, { [this.labelKey]: inputValue, [this.valueKey]: inputValue }].filter(
      option => typeof option === 'object',
    )
    this.setState({ options: newOptions, value: inputValue }, () => {
      onOptionCreated && onOptionCreated(inputValue)
    })
  }

  getSelectInput() {
    // The legacy string ref registered NOTHING without a `name` (ref={undefined} ⇒
    // this.refs[undefined] ⇒ null). Callers like the document upload drawer's
    // SupplierCustomerSelect rely on that: their blur() stays a no-op there.
    return (this.props.name && this.selectRef) || null
  }

  isValidNewOption(inputValue, selectValue, selectOptions) {
    if (inputValue.trim().length === 0 || selectOptions.find(option => option[this.labelKey] === inputValue)) {
      return false
    }

    return true
  }

  render() {
    const {
      index: key,
      name,
      label,
      containerClass,
      selectContainerClass,
      allowCreate,
      readOnly,
      labelReadOnly = readOnly,
      notAsync,
      infiniteScroll,
      dataQsId,
      customSelectOptions,
      customComponents,
      isInline,
      fillAnimationOptions,
      className,
      useFormik,
      customKey,
      errorMessage,
      hint,
      tooltip,
      cacheUniq,
      disableSearch,
      filterOption,
      showRequiredMark = false,
      customClassLabel,
    } = this.props
    const { value, options } = this.state

    const defaultOptions = {
      classNamePrefix: 'react-select',
      styles: this.getCustomStyles(),
      getOptionLabel: option => option[this.labelKey],
      getOptionValue: option => option[this.valueKey],
      noOptionsMessage: lang.selectInput.noOptionsMessage,
      formatCreateLabel: lang.selectInput.createLabel,
      formatOptionLabel: (option, { inputValue, context }) => {
        const optionLabel = this.renderOptionLabel(option, inputValue)

        // an option may carry its own tooltip, shown while hovering it in the open menu
        if (option.tooltip && context === 'menu') {
          return <Tooltip title={option.tooltip}>{optionLabel}</Tooltip>
        }

        return optionLabel
      },
      getNewOptionData: inputValue => {
        if (!inputValue) return

        return {
          [this.labelKey]: inputValue,
          [this.valueKey]: inputValue,
          isNewOption: true,
        }
      },
      onCreateOption: this.onOptionCreated,
      isValidNewOption: this.isValidNewOption,
      openOnFocus: true,
      closeOnSelect: true,
      placeholder: lang.selectInput.placeholder,
      loadingMessage: lang.selectInput.loadingMessage,
      clearValueText: lang.selectInput.clearValueText,
      searchPromptText: '',
      onBlur: this.onBlur,
      onFocus: this.onFocus,
      onChange: this.onInputChange,
      name,
      isSearchable: !disableSearch,
      filterOption,
    }

    const defaultComponents = {
      Arrow: () => {
        return <SVGIcon width="10px" height="10px" svg={ArrowIcon} />
      },
    }

    if (customSelectOptions?.toolTipKey) {
      defaultComponents.MultiValueContainer = MultiValueContainerWithToolTip
    }

    if (customSelectOptions?.menuWidth) {
      defaultComponents.Menu = MenuComponentWithWidth
    }

    const components = { ...defaultComponents, ...customComponents }
    // combine options
    const selectOptions = Object.assign({}, defaultOptions, customSelectOptions, { components })
    if (this.useDefault && options?.length) {
      selectOptions.defaultValue = options[0]
    }

    let select

    if (notAsync) {
      let selectedOption
      // Multi select with value as array of objects
      if (!!value && Array.isArray(value) && customSelectOptions?.isMulti) {
        selectedOption = value.filter(item => !!options.find(option => option[this.valueKey] === item[this.valueKey]))
      } else if (!!value && typeof value === 'object') { // Single select with value as object
        selectedOption = options.find(option => {
          if (option.options) {
            // we have to check the groups selection
            return option.options.find(subOption => subOption[this.valueKey] === value[this.valueKey])
          }
          return option[this.valueKey] === value[this.valueKey]
        })
        // we found in a grouped selection
        if (selectedOption?.options) {
          selectedOption = selectedOption.options.find(subOption => subOption[this.valueKey] === value[this.valueKey])
        }
      } else { // Single select with simple value (primitive data type)
        selectedOption = options?.find(option => option[this.valueKey] === value)
      }
      delete selectOptions.options

      select = allowCreate
        ? (
          <Creatable value={selectedOption} ref={this.setSelectRef} options={options} isDisabled={readOnly} {...selectOptions} />
        )
        : (
          <Select ref={this.setSelectRef} value={selectedOption} options={options} isDisabled={readOnly} {...selectOptions} />
        )
    } else {
      if (infiniteScroll) {
        select = allowCreate
          ? (
            <AsyncPaginateCreatable key={key || customKey} cacheUniqs={[cacheUniq]} value={value} ref={this.setSelectRef} isDisabled={readOnly} {...selectOptions} />
          )
          : (
            <AsyncPaginateSelect key={key || customKey} cacheUniqs={[cacheUniq]} value={value} ref={this.setSelectRef} isDisabled={readOnly} {...selectOptions} />
          )
      } else {
        select = allowCreate
          ? (
            <AsyncCreatable key={key || customKey} value={value} ref={this.setSelectRef} isDisabled={readOnly} {...selectOptions} />
          )
          : (
            <Async key={key || customKey} value={value} ref={this.setSelectRef} isDisabled={readOnly} {...selectOptions} />
          )
      }
    }

    const labelEl = !isInline && label
      ? <label className={clsx(styles.label, customClassLabel)} disabled={labelReadOnly}>{label}{showRequiredMark && <RequiredMark />}</label>
      : null
    const errorEl = useFormik
      ? <ErrorMessage name={name} className={styles.error} component="div" />
      : (errorMessage
        ? (
          <div className={styles.error}>
            <SVGIcon svg={ErrorIcon} width='12px' height='12px' fill={theme.red} className={styles.errorIcon} />
            <span>{errorMessage}</span>
          </div>
        )
        : null)

    const content = (
      <div className={clsx(styles.container, containerClass, className)} data-qs-id={dataQsId}>
        {labelEl}
        <div className={clsx(styles.selectContainer, selectContainerClass && selectContainerClass)}>
          <FillTransition {...fillAnimationOptions}>
            {select}
          </FillTransition>
          {hint && (<Tooltip
            title={hint}>
            <div className={styles.toolTipLogo}>
              <SVGIcon svg={IconHelp} width='25px' height='25px' />
            </div>
          </Tooltip>)}
        </div>
        {errorEl}
      </div>
    )

    // Hover tooltip on the whole control (same affordance as TextInput/DateInput's
    // `tooltip` prop) — unlike `hint`, no icon, so it fits compact layouts.
    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content
  }
}

SelectInput.propTypes = {
  /** Asterisk behind the label of a required field. */
  showRequiredMark: PropTypes.bool,
  readOnly: PropTypes.bool,
  labelReadOnly: PropTypes.bool,
  options: PropTypes.array,
  upwards: PropTypes.bool,
  styles: PropTypes.object,
  errorMessage: PropTypes.string,
  hint: PropTypes.string,
  tooltip: PropTypes.string,
  useFormik: PropTypes.bool,
  cacheUniq: PropTypes.string,
  dropDownMaxHeight: PropTypes.string,
  fillAnimationOptions: PropTypes.shape({
    show: PropTypes.bool,
    color: PropTypes.string,
  }),
  disableSearch: PropTypes.bool,
  filterOption: PropTypes.func,
  dataQsId: PropTypes.string.isRequired,
  customClassLabel: PropTypes.string,
}

SelectInput.contextType = FormikContext

SelectInput.defaultProps = {
  options: [],
  allowCreate: true,
  readOnly: false,
  customSelectOptions: {},
  upwards: true,
  styles: {},
  errorMessage: undefined,
  hint: undefined,
  tooltip: undefined,
  cacheUniq: '',
  createOnBlur: false,
  dropDownMaxHeight: undefined,
  fillAnimationOptions: {},
  disableSearch: false,
}

export default SelectInput
